import React, { useMemo, useState, useEffect } from 'react';
import {
  Sparkles, Filter, Loader, AlertTriangle, CheckCircle2,
  ChevronRight, X, BookOpen, ArrowRightLeft, RotateCcw, Pencil,
  Save, Check,
} from 'lucide-react';
import {
  useGetPropositionRepartitionIAQuery,
  useAppliquerRepartitionMutation,
  useAppliquerPropositionsIAMutation,
  PropositionClasseIA,
  UEProposeeIA,
  MatiereIA,
} from '../../services/uesApi';

// ─── Couleurs par nature d'UE ──────────────────────────────────────────────
const NATURE_META: Record<string, { label: string; bg: string; text: string; border: string }> = {
  UEF: { label: 'Fondamentale', bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', border: 'border-red-200 dark:border-red-800' },
  UEM: { label: 'Méthodologique', bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-200 dark:border-blue-800' },
  UET: { label: 'Transversale', bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-200 dark:border-green-800' },
  UED: { label: 'Découverte', bg: 'bg-gray-50 dark:bg-gray-900/30', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-200 dark:border-gray-800' },
};

const PROFIL_META: Record<string, { label: string; color: string }> = {
  SCIENTIFIQUE: { label: 'Scientifique', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300' },
  COMMERCIAL: { label: 'Commercial', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  JURIDIQUE: { label: 'Juridique', color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300' },
};

function statutCredits(total: number): { ok: boolean; icon: React.ReactNode; label: string } {
  if (total >= 28 && total <= 32) {
    return { ok: true, icon: <CheckCircle2 size={14} className="text-green-500" />, label: 'OK' };
  }
  return { ok: false, icon: <AlertTriangle size={14} className="text-orange-500" />, label: `${total} créd.` };
}

const RepartitionIA: React.FC = () => {
  const [filtreNiveau, setFiltreNiveau] = useState<string>('');
  const [filtrePole, setFiltrePole] = useState<string>('');
  const [filtreProfil, setFiltreProfil] = useState<string>('');
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [showConfirmAll, setShowConfirmAll] = useState(false);
  const [bulkResult, setBulkResult] = useState<{ applied: number; skipped: number; errors: number; message: string } | null>(null);

  const { data, isLoading, error, refetch } = useGetPropositionRepartitionIAQuery();
  const [appliquerToutes, { isLoading: applyingAll }] = useAppliquerPropositionsIAMutation();

  const handleAppliquerToutes = async () => {
    setBulkResult(null);
    try {
      const filtres: any = {};
      if (filtreNiveau) filtres.niveau = filtreNiveau;
      if (filtrePole) filtres.pole_code = filtrePole;
      const propositions = (data?.propositions || [])
        .filter(p => (!filtreNiveau || p.niveau === filtreNiveau)
                  && (!filtrePole || p.pole_code === filtrePole)
                  && (!filtreProfil || p.profil === filtreProfil))
        .map(p => ({
          option_id: p.option_id, niveau: p.niveau, semestre: p.semestre,
          ues: p.ues.map(u => ({
            code_ue: `UE${u.ordre}-${p.niveau}S${p.semestre}-${p.option_code}`,
            nom_ue: u.nom_suggere,
            ordre: u.ordre,
            matiere_ids: u.matieres.map(m => m.id),
          })),
        }));
      const res = await appliquerToutes({ filtres, propositions }).unwrap();
      setBulkResult({
        applied: res.applied, skipped: res.skipped,
        errors: res.errors?.length || 0, message: res.message,
      });
      setShowConfirmAll(false);
      refetch();
    } catch (e: any) {
      setBulkResult({
        applied: 0, skipped: 0, errors: 1,
        message: e?.data?.error || e?.message || 'Erreur application',
      });
      setShowConfirmAll(false);
    }
  };

  const propositionsFiltrees = useMemo(() => {
    if (!data) return [];
    return data.propositions.filter(p => {
      if (filtreNiveau && p.niveau !== filtreNiveau) return false;
      if (filtrePole && p.pole_code !== filtrePole) return false;
      if (filtreProfil && p.profil !== filtreProfil) return false;
      return true;
    });
  }, [data, filtreNiveau, filtrePole, filtreProfil]);

  const polesDispo = useMemo(() => {
    if (!data) return [];
    const set = new Set<string>();
    data.propositions.forEach(p => { if (p.pole_code) set.add(p.pole_code); });
    return Array.from(set).sort();
  }, [data]);

  const selectedProposition = useMemo(() => {
    if (!selectedKey || !data) return null;
    return data.propositions.find(p =>
      `${p.option_id}-${p.niveau}-${p.semestre}` === selectedKey
    ) || null;
  }, [selectedKey, data]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader className="animate-spin text-blue-500" size={32} />
        <span className="ml-3 text-gray-600 dark:text-gray-400">
          Génération des propositions IA pour les 91 classes...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <p className="text-red-700 dark:text-red-300">
          Erreur lors du chargement des propositions : {(error as any)?.status || 'inconnu'}
        </p>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
          <Sparkles className="text-purple-600 dark:text-purple-400" size={24} />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Répartition IA des UE
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {data.total_classes} classes analysées · classification automatique en UEF/UEM/UET · cible LMD 30 crédits
          </p>
        </div>
        <button
          onClick={() => setShowConfirmAll(true)}
          disabled={applyingAll}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-2 shadow"
          title="Appliquer toutes les propositions IA filtrées"
        >
          {applyingAll ? <Loader className="animate-spin" size={16} /> : <Check size={16} />}
          {applyingAll ? 'Application...' : 'Valider toutes les propositions'}
        </button>
      </div>

      {bulkResult && (
        <div className={`p-3 rounded border ${
          bulkResult.errors === 0
            ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-700'
            : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-700'
        }`}>
          <div className="flex items-center gap-2 text-sm">
            {bulkResult.errors === 0
              ? <CheckCircle2 className="text-green-600" size={16} />
              : <AlertTriangle className="text-red-600" size={16} />}
            <span className="font-medium">{bulkResult.message}</span>
            <button onClick={() => setBulkResult(null)} className="ml-auto text-gray-400 hover:text-gray-600">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {showConfirmAll && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <AlertTriangle className="text-orange-500" size={20} />
              Appliquer toutes les propositions ?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Cette action va créer/mettre à jour les UE et déplacer les matières pour
              <strong> {propositionsFiltrees.length} classe(s) </strong>
              selon les filtres actuels. Les UE devenues vides seront supprimées.
              <br/><br/>
              Les notes existantes ne sont jamais touchées (seul le rattachement matière→UE change).
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirmAll(false)}
                className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleAppliquerToutes}
                disabled={applyingAll}
                className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded flex items-center gap-2"
              >
                {applyingAll && <Loader className="animate-spin" size={14} />}
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {Object.entries(data.stats.nb_ues_distribution)
          .sort()
          .map(([k, v]) => {
            const nb = parseInt(k.split('_')[0]);
            const isOptimal = nb >= 4 && nb <= 5;
            return (
              <div key={k} className={`p-3 rounded-lg border ${
                isOptimal
                  ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800'
                  : 'bg-gray-50 border-gray-200 dark:bg-gray-800 dark:border-gray-700'
              }`}>
                <div className="text-xs text-gray-500 dark:text-gray-400">Classes à {nb} UE</div>
                <div className="text-xl font-bold text-gray-900 dark:text-gray-100">{v}</div>
                {isOptimal && <div className="text-[10px] text-green-600 dark:text-green-400">✓ Cible LMD</div>}
              </div>
            );
          })}
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap gap-3 items-center bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
        <Filter size={16} className="text-gray-400" />
        <select
          value={filtreNiveau}
          onChange={e => setFiltreNiveau(e.target.value)}
          className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Tous niveaux</option>
          <option value="L1">L1 (S1)</option>
          <option value="L2">L2 (S3)</option>
          <option value="L3">L3 (S5)</option>
        </select>
        <select
          value={filtrePole}
          onChange={e => setFiltrePole(e.target.value)}
          className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Tous pôles</option>
          {polesDispo.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select
          value={filtreProfil}
          onChange={e => setFiltreProfil(e.target.value)}
          className="px-3 py-1.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
        >
          <option value="">Tous profils</option>
          <option value="SCIENTIFIQUE">Scientifique</option>
          <option value="COMMERCIAL">Commercial</option>
          <option value="JURIDIQUE">Juridique</option>
        </select>
        <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
          {propositionsFiltrees.length} / {data.total_classes} affichées
        </span>
      </div>

      {/* Layout 2 colonnes : liste + détail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Liste des classes */}
        <div className="lg:col-span-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="max-h-[70vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                  <th className="px-3 py-2 font-medium">Option</th>
                  <th className="px-3 py-2 font-medium">Niveau</th>
                  <th className="px-3 py-2 font-medium">Profil</th>
                  <th className="px-3 py-2 font-medium text-center">UE</th>
                  <th className="px-3 py-2 font-medium text-center">Créd.</th>
                  <th className="px-3 py-2 font-medium text-center">⚠️</th>
                  <th className="px-3 py-2 w-6"></th>
                </tr>
              </thead>
              <tbody>
                {propositionsFiltrees.map(p => {
                  const key = `${p.option_id}-${p.niveau}-${p.semestre}`;
                  const selected = selectedKey === key;
                  const sc = statutCredits(p.total_credits);
                  const profMeta = PROFIL_META[p.profil];
                  return (
                    <tr
                      key={key}
                      onClick={() => setSelectedKey(key)}
                      className={`cursor-pointer border-b border-gray-100 dark:border-gray-700/50 transition-colors ${
                        selected
                          ? 'bg-purple-50 dark:bg-purple-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                      }`}
                    >
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                        {p.option_nom}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-gray-300 text-xs">
                        {p.niveau} · S{p.semestre}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${profMeta.color}`}>
                          {profMeta.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span className={`font-mono text-sm font-semibold ${
                          p.nb_ues >= 4 && p.nb_ues <= 5
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-orange-500'
                        }`}>
                          {p.nb_ues}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-center text-xs">
                        {p.total_credits_suggeres != null && p.total_credits_suggeres !== p.total_credits ? (
                          <span title={`Suggéré: ${p.total_credits_suggeres} (LMD = 30) · Actuel: ${p.total_credits}`}>
                            <span className="text-green-600 dark:text-green-400 font-semibold">{p.total_credits_suggeres}</span>
                            <span className="text-gray-400 mx-0.5">/</span>
                            <span className={`${sc.ok ? 'text-gray-500' : 'text-orange-500'} line-through opacity-60`}>{p.total_credits}</span>
                          </span>
                        ) : (
                          <span className={sc.ok ? 'text-gray-700 dark:text-gray-200' : 'text-orange-500 font-semibold'}>
                            {p.total_credits}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {p.non_placees_count > 0
                          ? <span className="text-orange-500 text-xs">{p.non_placees_count}</span>
                          : <span className="text-green-500 text-xs">✓</span>}
                      </td>
                      <td className="px-3 py-2">
                        <ChevronRight size={14} className="text-gray-400" />
                      </td>
                    </tr>
                  );
                })}
                {propositionsFiltrees.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center py-8 text-gray-500 dark:text-gray-400">
                      Aucune classe ne correspond aux filtres.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Détail de la classe sélectionnée */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          {selectedProposition ? (
            <DetailProposition
              proposition={selectedProposition}
              onClose={() => setSelectedKey(null)}
            />
          ) : (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <BookOpen size={40} className="mx-auto mb-3 opacity-30" />
              <p>Sélectionnez une classe pour voir le détail de la proposition UE.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Composant détail avec édition locale ──────────────────────────────────
// L'état des UE est maintenu localement (useState) pour permettre l'édition
// sans réappeler l'API. "Réinitialiser" revient à la proposition IA originale.
const DetailProposition: React.FC<{
  proposition: PropositionClasseIA;
  onClose: () => void;
}> = ({ proposition, onClose }) => {
  const profMeta = PROFIL_META[proposition.profil];

  // État local éditable des UE. Reset quand on change de classe.
  const [uesLocal, setUesLocal] = useState<UEProposeeIA[]>(proposition.ues);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmApply, setConfirmApply] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const [appliquer, { isLoading: applying }] = useAppliquerRepartitionMutation();

  useEffect(() => {
    setUesLocal(proposition.ues);
    setIsDirty(false);
    setConfirmApply(false);
    setFeedback(null);
  }, [proposition]);

  // Auto-clear feedback après 5s
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), 5000);
    return () => clearTimeout(t);
  }, [feedback]);

  const handleAppliquer = async () => {
    // Construire le payload attendu par l'endpoint existant appliquer_repartition
    const uesAffichees = uesLocal.filter(u => u.nb_matieres > 0);
    const payload = {
      niveau: proposition.niveau,
      semestre: proposition.semestre,
      option_id: proposition.option_id,
      ues: uesAffichees.map((ue, idx) => ({
        code_ue: `UE-${proposition.niveau}S${proposition.semestre}-${proposition.option_code}-${idx + 1}`,
        nom_ue: ue.nom_suggere,
        ordre_affichage: idx + 1,
        matiere_ids: ue.matieres.map(m => m.id),
      })),
    };

    try {
      const result = await appliquer(payload).unwrap();
      setFeedback({ type: 'success', msg: result.message || 'Répartition appliquée avec succès.' });
      setConfirmApply(false);
      setIsDirty(false);
    } catch (err: any) {
      const msg = err?.data?.error || err?.message || 'Erreur lors de l\'application';
      setFeedback({ type: 'error', msg });
      setConfirmApply(false);
    }
  };

  const totalCreditsLocal = useMemo(
    () => uesLocal.reduce((s, ue) => s + ue.credits, 0),
    [uesLocal]
  );

  const deplacerMatiere = (matiereId: number, ueSourceOrdre: number, ueCibleOrdre: number) => {
    if (ueSourceOrdre === ueCibleOrdre) return;

    setUesLocal(prev => {
      // Retrouver la matière dans l'UE source
      const ueSource = prev.find(u => u.ordre === ueSourceOrdre);
      const matiere = ueSource?.matieres.find(m => m.id === matiereId);
      if (!ueSource || !matiere) return prev;

      return prev.map(ue => {
        if (ue.ordre === ueSourceOrdre) {
          const nouvellesMatieres = ue.matieres.filter(m => m.id !== matiereId);
          return {
            ...ue,
            matieres: nouvellesMatieres,
            nb_matieres: nouvellesMatieres.length,
            credits: nouvellesMatieres.reduce((s, m) => s + (m.credits || 0), 0),
          };
        }
        if (ue.ordre === ueCibleOrdre) {
          const nouvellesMatieres = [...ue.matieres, matiere];
          return {
            ...ue,
            matieres: nouvellesMatieres,
            nb_matieres: nouvellesMatieres.length,
            credits: nouvellesMatieres.reduce((s, m) => s + (m.credits || 0), 0),
          };
        }
        return ue;
      });
    });
    setIsDirty(true);
  };

  const reinitialiser = () => {
    setUesLocal(proposition.ues);
    setIsDirty(false);
  };

  // UEs non vides (si on a déplacé toutes les matières d'une UE, elle disparaît visuellement)
  const uesAffichees = uesLocal.filter(ue => ue.nb_matieres > 0);

  return (
    <div className="flex flex-col h-full max-h-[70vh]">
      {/* Header */}
      <div className="sticky top-0 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border-b border-gray-200 dark:border-gray-700 p-4 z-10">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 truncate">
              {proposition.option_nom}
            </h2>
            <div className="flex items-center gap-2 mt-1 text-xs">
              <span className="text-gray-600 dark:text-gray-400">
                {proposition.niveau} · S{proposition.semestre}
              </span>
              <span className={`px-2 py-0.5 rounded font-medium ${profMeta.color}`}>
                {profMeta.label}
              </span>
              {isDirty && (
                <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
                  <Pencil size={10} className="inline mr-1" />
                  Modifié
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isDirty && (
              <button
                onClick={reinitialiser}
                className="p-1.5 text-xs flex items-center gap-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600"
                title="Réinitialiser à la proposition IA"
              >
                <RotateCcw size={12} /> Reset
              </button>
            )}
            <button
              onClick={() => setConfirmApply(true)}
              disabled={applying || uesAffichees.length === 0}
              className="p-1.5 text-xs flex items-center gap-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded font-medium"
              title="Créer ces UE en base de données"
            >
              {applying
                ? <Loader size={12} className="animate-spin" />
                : <Save size={12} />
              }
              Appliquer
            </button>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Feedback succès/erreur */}
        {feedback && (
          <div className={`mt-2 text-xs p-2 rounded flex items-start gap-2 ${
            feedback.type === 'success'
              ? 'bg-green-50 text-green-800 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-800'
              : 'bg-red-50 text-red-800 dark:bg-red-900/30 dark:text-red-200 border border-red-200 dark:border-red-800'
          }`}>
            {feedback.type === 'success' ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
            <span className="flex-1">{feedback.msg}</span>
            <button onClick={() => setFeedback(null)} className="opacity-60 hover:opacity-100">
              <X size={12} />
            </button>
          </div>
        )}

        {/* Confirmation d'application */}
        {confirmApply && (
          <div className="mt-2 text-xs p-3 rounded bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800">
            <div className="font-medium text-amber-900 dark:text-amber-200 mb-2">
              Créer {uesAffichees.length} UE en base pour cette classe ?
            </div>
            <div className="text-amber-700 dark:text-amber-300 mb-2">
              Les anciennes UE de {proposition.niveau} S{proposition.semestre} / {proposition.option_nom} seront remplacées.
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAppliquer}
                disabled={applying}
                className="px-3 py-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded text-xs font-medium flex items-center gap-1"
              >
                {applying ? <Loader size={12} className="animate-spin" /> : <Check size={12} />}
                Confirmer
              </button>
              <button
                onClick={() => setConfirmApply(false)}
                disabled={applying}
                className="px-3 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded text-xs border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
        <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
          <div className="bg-white dark:bg-gray-700 rounded p-2 text-center">
            <div className="text-gray-500 dark:text-gray-400">UE</div>
            <div className="font-bold text-gray-900 dark:text-gray-100">{uesAffichees.length}</div>
          </div>
          <div className="bg-white dark:bg-gray-700 rounded p-2 text-center">
            <div className="text-gray-500 dark:text-gray-400">Matières</div>
            <div className="font-bold text-gray-900 dark:text-gray-100">{proposition.total_matieres}</div>
          </div>
          <div className="bg-white dark:bg-gray-700 rounded p-2 text-center">
            <div className="text-gray-500 dark:text-gray-400">Crédits</div>
            <div className={`font-bold ${
              totalCreditsLocal >= 28 && totalCreditsLocal <= 32
                ? 'text-green-600 dark:text-green-400'
                : 'text-orange-500'
            }`}>
              {totalCreditsLocal}
            </div>
          </div>
        </div>
      </div>

      {/* Liste des UE éditables */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {uesAffichees.map(ue => (
          <UECard
            key={ue.ordre}
            ue={ue}
            toutesUEs={uesAffichees}
            onDeplacerMatiere={deplacerMatiere}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Carte d'une UE avec bouton "Déplacer" par matière ─────────────────────
const UECard: React.FC<{
  ue: UEProposeeIA;
  toutesUEs: UEProposeeIA[];
  onDeplacerMatiere: (matiereId: number, sourceOrdre: number, cibleOrdre: number) => void;
}> = ({ ue, toutesUEs, onDeplacerMatiere }) => {
  const meta = NATURE_META[ue.nature];
  return (
    <div className={`rounded-lg border p-3 ${meta.bg} ${meta.border}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${meta.text} bg-white dark:bg-black/30`}>
            UE{ue.ordre}
          </span>
          <span className={`text-[10px] font-medium ${meta.text}`}>
            [{ue.nature}] {meta.label}
          </span>
          {ue.is_tp && (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300">
              TP
            </span>
          )}
        </div>
        <span className={`text-xs font-mono font-semibold ${meta.text} text-right`}>
          {ue.credits_suggeres != null && ue.credits_suggeres !== ue.credits ? (
            <>
              <span className="text-green-600 dark:text-green-400 font-bold">{ue.credits_suggeres}</span>
              <span className="text-gray-400 mx-0.5">/</span>
              <span className="line-through opacity-50">{ue.credits}</span>
              <span className="text-[9px] ml-1 opacity-70">cr.</span>
            </>
          ) : (
            <>{ue.credits} créd.</>
          )}
          {' · '}{ue.nb_matieres} mat.
        </span>
      </div>
      <div className={`text-sm font-semibold ${meta.text} mb-2`}>
        {ue.nom_suggere}
      </div>
      <div className="space-y-1">
        {ue.matieres.map(m => (
          <MatiereRow
            key={m.id}
            matiere={m}
            ueCourante={ue}
            autresUEs={toutesUEs.filter(u => u.ordre !== ue.ordre)}
            onDeplacer={(cibleOrdre) => onDeplacerMatiere(m.id, ue.ordre, cibleOrdre)}
          />
        ))}
      </div>
    </div>
  );
};

// ─── Ligne d'une matière avec dropdown "Déplacer" ──────────────────────────
const MatiereRow: React.FC<{
  matiere: MatiereIA;
  ueCourante: UEProposeeIA;
  autresUEs: UEProposeeIA[];
  onDeplacer: (cibleOrdre: number) => void;
}> = ({ matiere, autresUEs, onDeplacer }) => {
  const [ouvert, setOuvert] = useState(false);

  // Fermer le menu si on clique à l'extérieur
  useEffect(() => {
    if (!ouvert) return;
    const handler = () => setOuvert(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [ouvert]);

  return (
    <div className="flex items-center gap-2 text-xs bg-white/50 dark:bg-black/20 rounded px-2 py-1 group relative">
      <span className="flex-1 text-gray-700 dark:text-gray-200">
        {matiere.nom}
        {matiere.famille_label && (
          <span className="ml-2 text-[10px] text-gray-400 italic">
            → {matiere.famille_label}
          </span>
        )}
      </span>
      <span className="text-gray-500 dark:text-gray-400 font-mono text-[10px] whitespace-nowrap">
        {matiere.credits ?? '?'} cr.
      </span>
      {autresUEs.length > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); setOuvert(v => !v); }}
          className="p-1 rounded text-gray-400 hover:text-blue-600 hover:bg-white dark:hover:bg-gray-700 opacity-0 group-hover:opacity-100 transition-opacity"
          title="Déplacer vers une autre UE"
        >
          <ArrowRightLeft size={12} />
        </button>
      )}
      {ouvert && (
        <div
          className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg min-w-[200px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase border-b border-gray-100 dark:border-gray-700">
            Déplacer vers
          </div>
          {autresUEs.map(cible => {
            const cibleMeta = NATURE_META[cible.nature];
            return (
              <button
                key={cible.ordre}
                onClick={() => { onDeplacer(cible.ordre); setOuvert(false); }}
                className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 border-b border-gray-50 dark:border-gray-700/50 last:border-b-0"
              >
                <div className={`font-medium ${cibleMeta.text}`}>
                  UE{cible.ordre} [{cible.nature}]
                </div>
                <div className="text-gray-600 dark:text-gray-300 truncate">
                  {cible.nom_suggere}
                </div>
                <div className="text-[10px] text-gray-400">
                  {cible.nb_matieres} matière{cible.nb_matieres > 1 ? 's' : ''} · {cible.credits} créd.
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RepartitionIA;
